// PWA Installation Debugging Utility
// This helps identify why the install prompt might not be appearing automatically

export interface PWADiagnostics {
  hasManifest: boolean;
  manifestValid: boolean;
  hasServiceWorker: boolean;
  serviceWorkerRegistered: boolean;
  isHTTPS: boolean;
  hasIcons: boolean;
  iconsValid: boolean;
  meetsCriteria: boolean;
  reasons: string[];
}

export async function diagnosePWAInstallability(): Promise<PWADiagnostics> {
  const diagnostics: PWADiagnostics = {
    hasManifest: false,
    manifestValid: false,
    hasServiceWorker: false,
    serviceWorkerRegistered: false,
    isHTTPS: false,
    hasIcons: false,
    iconsValid: false,
    meetsCriteria: false,
    reasons: [],
  };

  // Check HTTPS
  diagnostics.isHTTPS = 
    window.location.protocol === 'https:' || 
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1';
  
  if (!diagnostics.isHTTPS) {
    diagnostics.reasons.push('Not served over HTTPS (required for PWA)');
  }

  // Check Manifest
  try {
    const manifestResponse = await fetch('/site.webmanifest');
    diagnostics.hasManifest = manifestResponse.ok;
    
    if (diagnostics.hasManifest) {
      const manifest = await manifestResponse.json();
      diagnostics.manifestValid = !!(
        manifest.name &&
        manifest.short_name &&
        manifest.icons &&
        manifest.icons.length > 0 &&
        manifest.start_url &&
        manifest.display
      );

      if (!diagnostics.manifestValid) {
        diagnostics.reasons.push('Manifest is missing required fields (name, short_name, icons, start_url, display)');
      }

      // Check icons
      if (manifest.icons && manifest.icons.length > 0) {
        diagnostics.hasIcons = true;
        
        // Check for required icon sizes (192x192 and 512x512)
        const has192 = manifest.icons.some((icon: any) => 
          icon.sizes && (icon.sizes.includes('192') || icon.sizes === '192x192')
        );
        const has512 = manifest.icons.some((icon: any) => 
          icon.sizes && (icon.sizes.includes('512') || icon.sizes === '512x512')
        );
        
        diagnostics.iconsValid = has192 && has512;
        
        if (!diagnostics.iconsValid) {
          diagnostics.reasons.push('Manifest missing required icon sizes (192x192 and/or 512x512)');
        }
      } else {
        diagnostics.reasons.push('Manifest has no icons defined');
      }
    } else {
      diagnostics.reasons.push('Manifest file not accessible at /site.webmanifest');
    }
  } catch (error) {
    diagnostics.reasons.push(`Error checking manifest: ${error}`);
  }

  // Check Service Worker
  diagnostics.hasServiceWorker = 'serviceWorker' in navigator;
  
  if (diagnostics.hasServiceWorker) {
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      diagnostics.serviceWorkerRegistered = !!registration;
      
      if (!diagnostics.serviceWorkerRegistered) {
        diagnostics.reasons.push('Service worker not registered. It may still be registering...');
      }
    } catch (error) {
      diagnostics.reasons.push(`Error checking service worker: ${error}`);
    }
  } else {
    diagnostics.reasons.push('Service workers not supported in this browser');
  }

  // Check if already installed
  const isInstalled = window.matchMedia('(display-mode: standalone)').matches;
  if (isInstalled) {
    diagnostics.reasons.push('App is already installed');
  }

  // Overall assessment
  diagnostics.meetsCriteria = 
    diagnostics.isHTTPS &&
    diagnostics.hasManifest &&
    diagnostics.manifestValid &&
    diagnostics.hasIcons &&
    diagnostics.iconsValid &&
    diagnostics.serviceWorkerRegistered &&
    !isInstalled;

  if (!diagnostics.meetsCriteria) {
    if (diagnostics.reasons.length === 0) {
      diagnostics.reasons.push('Unknown issue - check browser console for details');
    }
  }

  return diagnostics;
}

export function logPWADiagnostics(diagnostics: PWADiagnostics): void {
  const status = (ok: boolean) => (ok ? 'ok' : 'fail');

  console.group('[PWA] Installation Diagnostics');
  console.log('Meets PWA Criteria:', diagnostics.meetsCriteria ? 'yes' : 'no');
  console.log('HTTPS:', status(diagnostics.isHTTPS));
  console.log('Manifest:', status(diagnostics.hasManifest && diagnostics.manifestValid));
  console.log('Icons:', status(diagnostics.hasIcons && diagnostics.iconsValid));
  console.log('Service Worker:', status(diagnostics.serviceWorkerRegistered));

  if (diagnostics.reasons.length > 0) {
    console.group('Issues found:');
    diagnostics.reasons.forEach((reason) => console.warn('[issue]', reason));
    console.groupEnd();
  }

  console.groupEnd();
}
